import React, { useEffect, useMemo, useState } from 'react';
import "./LateCheckout.css";
import Header from '../../components/Header';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from "../../../customHooks/useAuth.js"
import { toast } from 'react-toastify';
import PageLoader from '../../components/PageLoader/PageLoader';
import axiosInstance from '../../../axiosInstance/axiosInstance.js';
import PromoSection, { computeDiscount } from '../FoodSummary/PromoSection';
import { HOTEL_TIMEZONE } from "../../../common/hotelTime";
import { taxOn } from "../../../common/gst";
import { formatMoney } from "../../../common/money";

const OPEN_REQUEST_STATUSES = [
    "Pending_Hotel",
    "Pending_Guest",
    "InProgress_Hotel",
    "Scheduled_Hotel",
];

const LATE_CHECKOUT_TYPE = "PostCheckin Late Checkout Request";

export default function LateCheckout() {

    const location = useLocation();
    const navigate = useNavigate();

    const hotelId = location.state?.hotelId || "";
    const bookingId = location.state?.bookingId || "";
    const bookedRooms = useMemo(() => location.state?.bookedRooms || [], [location.state]);

    // Need to Send to Hotel View
    const hotelName = location.state?.hotelName || "";
    const hotelAddress = location.state?.hotelAddress || "";
    const checkInDate = location.state?.checkInDate || "";
    const checkOutDate = location.state?.checkOutDate || "";
    const status = location.state?.status || "";
    const activeTab = location.state?.activeTab || "";
    const booking_code = location.state?.booking_code || "";

    // For Refresh Handle
    useAuth();

    const userId = localStorage.getItem("user_id");

    const [hourlyRate, setHourlyRate] = useState(null);
    const [maxHours, setMaxHours] = useState(null);
    const [gstPercentage, setGstPercentage] = useState(0);
    const [isLoadingConfig, setIsLoadingConfig] = useState(true);

    const [selectedRoomId, setSelectedRoomId] = useState("");
    const [hours, setHours] = useState(1);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [appliedPromo, setAppliedPromo] = useState(null);
    const handleApplyPromo = (offer) => setAppliedPromo(offer);
    const handleRemovePromo = () => setAppliedPromo(null);

    useEffect(() => {
        if (hotelId === "" || bookingId === "") {
            navigate("/bookings");
        }
    }, [hotelId, bookingId, navigate]);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await axiosInstance.get(`/api/v1/hotels/${hotelId}/late-checkout-info`);
                const rate = response?.data?.late_checkout_hourly_rate;
                const max = response?.data?.late_checkout_max_hours;
                if (rate === null || rate === undefined || max === null || max === undefined) {
                    toast.error("Your Hotel Has Not Configured Late Checkout Yet");
                    navigate("/bookings");
                    return;
                }
                setHourlyRate(rate);
                setMaxHours(max);
                setGstPercentage(response?.data?.late_checkout_gst_percentage ?? 0);
            } catch (error) {
                toast.error(error?.response?.data?.message || "Could Not Load Late Checkout Details");
                navigate("/bookings");
            } finally {
                setIsLoadingConfig(false);
            }
        };
        if (hotelId !== "") {
            fetchConfig();
        }
    }, [hotelId, navigate]);

    const hasOpenLateCheckout = (room) =>
        (room.requests || []).some(
            (request) => request.type === LATE_CHECKOUT_TYPE && OPEN_REQUEST_STATUSES.includes(request.status)
        );

    const isRoomSelectable = (room) =>
        room.is_checkin_completed && !room.is_checkout_completed && !hasOpenLateCheckout(room);

    // Every room of the booking is listed, the ones that cannot take a late
    // checkout carrying the reason — a guest who sees only an empty picker has
    // no way to tell why their room is missing.
    const sortedRooms = useMemo(
        () => [...bookedRooms].sort((a, b) => Number(a.room_number) - Number(b.room_number)),
        [bookedRooms]
    );

    const blockedReason = (room) => {
        if (room.is_checkout_completed) return "Checked out";
        if (!room.is_checkin_completed) return "Not checked in";
        if (hasOpenLateCheckout(room)) return "Already requested";
        return "";
    };

    const selectedRoom = bookedRooms.find((room) => room.id === selectedRoomId);

    const grossAmount = useMemo(
        () => (hourlyRate === null ? 0 : hours * hourlyRate),
        [hours, hourlyRate]
    );

    const discountAmount = useMemo(
        () => (appliedPromo ? computeDiscount(appliedPromo, grossAmount) : 0),
        [appliedPromo, grossAmount]
    );

    const taxAmount = taxOn(grossAmount - discountAmount, gstPercentage);
    const payableAmount = grossAmount - discountAmount + taxAmount;

    const newCheckOutTime = useMemo(() => {
        if (!selectedRoom?.checkout_date) return "";
        const date = new Date(selectedRoom.checkout_date);
        date.setHours(date.getHours() + hours);
        return date.toLocaleString("en-IN", {
            timeZone: HOTEL_TIMEZONE,
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    }, [selectedRoom, hours]);

    const decreaseHours = () => setHours((current) => Math.max(1, current - 1));
    const increaseHours = () => setHours((current) => Math.min(maxHours || 1, current + 1));

    const handleRoomSelect = (room) => {
        if (!isRoomSelectable(room)) return;
        setSelectedRoomId(room.id);
        setErrors({ ...errors, roomId: "" });
    };

    const handleSave = async () => {
        if (!selectedRoomId) {
            setErrors({ roomId: "Room selection is required" });
            return;
        }

        setIsSubmitting(true);
        try {
            const passData = {
                hours_late: hours,
                promo_code: appliedPromo?.code || ""
            };
            await axiosInstance.post(
                `/api/v1/users/${userId}/bookings/${bookingId}/booked-rooms/${selectedRoomId}/guest-requests/post-checkin/late-checkout-request/`,
                passData
            );
            toast.success("Late Checkout Request Created Successfully");
            navigate("/bookings", { state: { activeRequests: "requests" } });
        } catch (error) {
            toast.error(error?.response?.data?.message || "Some Error Occured");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {(isSubmitting || isLoadingConfig) && <PageLoader />}
            <div className="lateCheckout">
                <Header title="Late Checkout" para="Pick how much longer you need the room. The fee is charged per hour."
                    hotelName={hotelName}
                    hotelAddress={hotelAddress}
                    checkInDate={checkInDate}
                    checkOutDate={checkOutDate}
                    status={status}
                    activeTab={activeTab}
                    hotelId={hotelId}
                    bookingId={bookingId}
                    booking_code={booking_code}
                    movetoHotel="movetoHotel"
                />

                <div className="lateCheckout-body">

                    <div className="lateCheckout-field">
                        <h6>Which room needs the late checkout?</h6>
                        <div className="lateCheckout-rooms">
                            {sortedRooms.map((room, index) => {
                                const selectable = isRoomSelectable(room);
                                const selected = room.id === selectedRoomId;
                                return (
                                    <div
                                        key={room.id || index}
                                        className={`lateCheckout-roomCard${selected ? " is-selected" : ""}${selectable ? "" : " is-blocked"}`}
                                        onClick={() => handleRoomSelect(room)}
                                    >
                                        <div className="lateCheckout-roomCard-head">
                                            <span className="lateCheckout-roomCard-radio" />
                                            <span className="lateCheckout-roomCard-name">
                                                Room {room.room_number}
                                            </span>
                                            {!selectable && (
                                                <span className="lateCheckout-roomCard-tag">
                                                    {blockedReason(room)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {sortedRooms.length === 0 && (
                            <span className="lateCheckout-hint">
                                No rooms on this booking
                            </span>
                        )}
                        {errors.roomId && <span className="error">{errors.roomId}</span>}
                    </div>

                    <div className="lateCheckout-field">
                        <h6>How much longer do you need the room?</h6>
                        <div className="lateCheckout-hours">
                            <div className="qtyButton" onClick={decreaseHours}>-</div>
                            <div className="qty">{hours} {hours === 1 ? "hour" : "hours"}</div>
                            <div className="qtyButton" onClick={increaseHours}>+</div>
                        </div>
                        {maxHours !== null && <span className="lateCheckout-hint">Up to {maxHours} hours late</span>}
                        {newCheckOutTime && <span className="lateCheckout-hint">Check out by {newCheckOutTime}</span>}
                    </div>

                    <div className="lateCheckout-fee">
                        <div className="lateCheckout-fee-row">
                            <span>{hours} × ₹{hourlyRate || 0}</span>
                            <span>₹{formatMoney(grossAmount)}</span>
                        </div>
                        {discountAmount > 0 && (
                            <div className="lateCheckout-fee-row lateCheckout-fee-discount">
                                <span>Discount</span>
                                <span>- ₹{formatMoney(discountAmount)}</span>
                            </div>
                        )}
                        {taxAmount > 0 && (
                            <div className="lateCheckout-fee-row">
                                <span>GST ({gstPercentage}%)</span>
                                <span>+ ₹{formatMoney(taxAmount)}</span>
                            </div>
                        )}
                        <div className="lateCheckout-fee-row lateCheckout-fee-total">
                            <span>Late checkout fee</span>
                            <span>₹{formatMoney(payableAmount)}</span>
                        </div>
                    </div>

                </div>

                {/* Promo Code */}
                <PromoSection
                    userId={userId}
                    hotelId={hotelId}
                    service={LATE_CHECKOUT_TYPE}
                    orderAmount={grossAmount}
                    appliedPromo={appliedPromo}
                    discountAmount={discountAmount}
                    onApply={handleApplyPromo}
                    onRemove={handleRemovePromo}
                    previewDiscount={true}
                />

                <div className='lateCheckout-btn'>
                    <button onClick={handleSave} disabled={selectedRoomId === "" || isSubmitting}>
                        Request Late Checkout
                    </button>
                </div>

            </div>
        </>
    )
}
